import { Component, OnInit, OnDestroy, signal, WritableSignal, ChangeDetectorRef } from '@angular/core';
import { SecurityService } from '../../services/security.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModalComponent } from "../../shared/modal/modal.component";
import { LoaderService } from '../../services/loader.service';
import { RouteService } from '../../services/route.service';
import { HelpService } from '../../services/help.service';

@Component({
  selector: 'app-sign',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
  ],
  templateUrl: './sign.component.html',
  styleUrl: './sign.component.css'
})
export class SignComponent implements OnInit, OnDestroy {
  
  form: FormGroup;
  seconds: number = 120;
  showModal = signal(true);
  message = signal('');
  intervalId: any;
  showResendButton = false;
  srcImg = 'svg/otp.svg';
  nextRoute: WritableSignal<string> = signal('');
  

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _loaderService: LoaderService,
    private _routeService: RouteService,
    private _helpService: HelpService,
    private _cdr: ChangeDetectorRef
  ){
      this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');
      this._title.setTitle('Onboarding Digital | Firma Vinculación');
      this.form = new FormGroup({
        code1: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code2: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code3: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code4: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code5: new FormControl('', [Validators.required, Validators.maxLength(1)]),
        code6: new FormControl('', [Validators.required, Validators.maxLength(1)])
      });
  }

  ngOnInit(): void {
    this._helpService.show();
    this.getOTP();
    this._loaderService.hide();
    this.startCountdown();
  }

  getOTP() {
    this._securityService.sendOTP().subscribe({
      next: (response: any) => {
        
          this.message.set(`Se ha enviado el código de verificación a su celular ${response.startPhone}*****${response.lastPhone}`);
          this.seconds = 120;
          this.showModal.set(true);
          this._cdr.detectChanges();
        }
    });
  }

  startCountdown() {
    this.showResendButton = false;
    this.seconds = 120;
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
        this._cdr.detectChanges();
      } else {
        this.showResendButton = true;
        clearInterval(this.intervalId);
        this._cdr.detectChanges();
      }
    }, 1000);
  }

  closeModal() {
    this.showModal.set(false);
    this._cdr.detectChanges(); // Forzar detección de cambios
  }

  // Método para manejar el input automático entre campos
  onInput(event: any, index: number) {
    const input = event.target;
    const value = input.value;
    
    // Si se ingresó un valor y no es el último campo, mover al siguiente
    if (value && index < 6) {
      const nextInput = document.getElementById(`code${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }
    
    // Si se presiona backspace y el campo está vacío, retroceder al anterior
    if (event.inputType === 'deleteContentBackward' && !value && index > 1) {
      const prevInput = document.getElementById(`code${index - 1}`);
      if (prevInput) {
        (prevInput as HTMLInputElement).focus();
      }
    }
  }

  // Método para permitir solo números
  onKeyPress(event: KeyboardEvent) {
    const charCode = event.key;
    if (!/\d/.test(charCode)) {
      event.preventDefault();
    }
  }

  // Obtener el código completo concatenando los 6 dígitos
  getFullCode(): string {
    return [
      this.form.value.code1,
      this.form.value.code2,
      this.form.value.code3,
      this.form.value.code4,
      this.form.value.code5,
      this.form.value.code6
    ].join('');
  }

  // Verificar si el formulario es válido (todos los campos tienen un dígito)
  isFormValid(): boolean {
    return this.form.valid && this.getFullCode().length === 6;
  }

  onSubmit() {
    if (this.isFormValid()) {
      const fullCode = this.getFullCode();
      this._securityService.validateOTP(fullCode).subscribe({
        next: (response: any) => {
          if(response.checkpoint == 'SIGNED_11') {
            this._router.navigateByUrl(this.nextRoute());
          } else {
            this.srcImg = 'svg/phone-chat.svg';
            this.message.set('Código incorrecto. Por favor, inténtelo de nuevo.');
            this.showModal.set(true);
            this._cdr.detectChanges();
            this.clearForm();
          }
        },
        error: (error: any) => {
          this.srcImg = 'svg/phone-chat.svg';
          this.message.set('Error al validar el código. Por favor, inténtelo de nuevo.');
          this.showModal.set(true);
          this._cdr.detectChanges();
          this.clearForm();
        }
      });
    } else {
      this.message.set('Por favor, complete todos los dígitos del código de verificación.');
      this.showModal.set(true);
      this._cdr.detectChanges(); // Forzar detección de cambios
    }
  }

  clearForm() {
    this.form.patchValue({
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: ''
    });
    // Enfocar el primer campo
    const firstInput = document.getElementById('code1');
    if (firstInput) {
      (firstInput as HTMLInputElement).focus();
    }
    this._cdr.detectChanges(); // Forzar detección de cambios
  }

  resendCode() {
    if (this.seconds <= 0) {
      this.getOTP();
      this.startCountdown();
    }
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}